import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { OrderCard } from "@/components/staff/OrderCard";
import { api, errMsg, rupiah, STATUS } from "@/lib/api";

const Stat = ({ label, value, testid }) => (
  <div className="card-paper p-5"><div className="text-xs text-muted2">{label}</div><div className="text-2xl font-bold mt-1" data-testid={testid}>{value}</div></div>
);

export const OrdersBoard = () => {
  const [filter, setFilter] = useState("aktif");
  const [outlet, setOutlet] = useState("");
  const [outlets, setOutlets] = useState([]);

  useEffect(() => {
    api.get("/admin/outlets").then((r) => setOutlets(r.data)).catch(() => {});
  }, []);
  const [q, setQ] = useState("");
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/orders", { params: { status: filter, outlet } }).then((r) => setOrders(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/summary", { params: { outlet } }).then((r) => setSummary(r.data)).catch(() => {});
  }, [filter, outlet]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const update = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status });
      toast.success(`Status: ${STATUS[status].label}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const digits = q.replace(/\D/g, "");
  const shown = orders.filter((o) => !q.trim() || o.code.toLowerCase().includes(q.trim().toLowerCase()) || (digits && String(o.total).includes(digits)));

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="Pesanan hari ini" value={summary?.orders_today ?? "-"} testid="stat-orders-today" />
        <Stat label="Perlu cek pembayaran" value={summary?.awaiting_confirmation ?? "-"} testid="stat-awaiting" />
        <Stat label="Pendapatan terkonfirmasi hari ini" value={summary ? rupiah(summary.revenue_today) : "-"} testid="stat-revenue" />
        <Stat label="Pajak terkumpul hari ini" value={summary ? rupiah(summary.tax_today) : "-"} testid="stat-tax" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-8">
        <h2 className="font-serif font-semibold text-2xl">Pesanan masuk</h2>
        <div className="flex flex-wrap items-center gap-2">
          <select value={outlet} onChange={(e) => setOutlet(e.target.value)} className="px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm" data-testid="staff-outlet-filter">
            <option value="">Semua gerai</option>
            {outlets.map((o) => <option key={o.code} value={o.code}>{o.code} · {o.name}</option>)}
          </select>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nominal (mis. 100223) / kode" data-testid="staff-order-search"
            className="w-64 px-3 py-2 rounded-lg border border-sage-200 bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 bg-paper" data-testid="staff-order-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="aktif">Aktif</SelectItem>
              {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              <SelectItem value="semua">Semua</SelectItem>
            </SelectContent>
          </Select>
          <button onClick={load} className="p-2.5 rounded-lg border border-sage-200 bg-paper hover:bg-sage-100" data-testid="staff-orders-refresh"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>
      {shown.length === 0 && <p className="text-center text-muted2 py-16" data-testid="staff-orders-empty">{q ? "Tidak ada pesanan dengan nominal/kode itu." : "Belum ada pesanan."}</p>}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5">
        {shown.map((o) => <OrderCard key={o.id} order={o} onUpdate={update} />)}
      </div>
    </div>
  );
};
